import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { green } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpPsiDetailService from "@plannel/services/fp/fp-psi-detail-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult/MaterialPsi 파일럿에서 실제 확인된
// 값(9)을 그대로 재사용한다(가이드 §A-3c, 테넌트 단일값이라 화면별로 다르지 않다).
// TODO(MIG-T2): [psidetail/PsiDetail] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드는 stageCdNm/itemCdNm 2개였으나(loadData/
// loadChartData의 getValues 기준), 백엔드(ProdSalesStockStatusQueryRepository#makeWhereClause)를
// 대조한 결과 stageCdNm은 어느 where절에도 없어 애초에 필터로 동작하지 않는 죽은 파라미터였다
// (itemCdNm만 실제 contains 조건으로 소비됨) — ResrcPlanResult 파일럿의 "SearchOptsGroup 렌더링만
// 되고 값이 전달 안 됨" 선례와 동일 판단으로 stageCdNm 필드 자체를 두지 않는다(기능 유실 아님,
// 죽은 UI를 옮기지 않는 것).
const SEARCH_FIELD_LABELS = { itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old analysisIndicator 볼드/강조 대상(styleCallback의 text-font-bold 대응) — Tier1, 행 단위로 재현.
const FP_BEGINNING_STOCK = "FP_BEGINNING_STOCK";
const FP_ENDING_STOCK = "FP_ENDING_STOCK";
const FP_GROUP_TOTAL = "FP_GROUP_TOTAL";

// old PsiDetailChart.jsx INDICATOR_CONFIG 이식 — 백엔드(ProdSalesStockStatusService#getPsiData,
// isDetail=true)가 그룹마다 이 지표들을 leaf 행으로 내려준다. FP_GROUP_TOTAL은 old INDICATOR_CONFIG에도
// 없어(합계 행은 그리드 전용, 차트엔 안 그림) 이 맵에 없는 지표는 차트에서 제외한다(old `if (!conf)
// return null` 그대로). old는 지표별로 고유 테마 색(themeStore.t3smartscm.fpChartPriority)을 썼지만
// new엔 그 테마 값이 없어 PivotChartComposed 기본 팔레트(colorByMeasure)에 맡긴다(색상 근사, Tier1
// 범위 밖 장식 디테일).
// direction:'minus'인 소비 지표는 프론트에서 값을 음수로 뒤집어 기초/기말재고 라인을 기준으로
// 위(공급)/아래(소비) 발란스 형태로 그린다(old makeChartDataset의 direction 대응, MaterialPsi와 동일 원리).
// old는 기초/기말재고 라인 + 공급/소비 막대를 전부 같은 왼쪽 "(QTY)" 축에 그리고, FP_OUTPUT_SHIP만
// 별도 오른쪽 "FP_OUTPUT_SHIP(QTY)" 축에 그렸다(PsiDetailChart.jsx의 customTitle.y.left/right 대응).
const INDICATOR_ORDER = [
  FP_BEGINNING_STOCK,
  "FP_INPUT_PRODUCT", "FP_INPUT_RECEPT", "FP_INPUT_IN_PROCESS", "FP_INPUT_BY_PRODUCT",
  "FP_OUTPUT_PRODUCT", "FP_OUTPUT_RECEPT", "FP_OUTPUT_EXPIRE", "FP_OUTPUT_IN_PROCESS", "FP_OUTPUT_BY_PRODUCT",
  "FP_OUTPUT_SHIP",
  FP_ENDING_STOCK
];
const INDICATOR_CONFIG = {
  [FP_BEGINNING_STOCK]: { type: "line", orientation: "left", dashed: false, direction: "plus" },
  [FP_ENDING_STOCK]: { type: "line", orientation: "left", dashed: true, direction: "plus" },
  FP_INPUT_PRODUCT: { type: "bar", orientation: "left", stackId: "supply", direction: "plus" },
  FP_INPUT_RECEPT: { type: "bar", orientation: "left", stackId: "supply", direction: "plus" },
  FP_INPUT_IN_PROCESS: { type: "bar", orientation: "left", stackId: "supply", direction: "plus" },
  FP_INPUT_BY_PRODUCT: { type: "bar", orientation: "left", stackId: "supply", direction: "plus" },
  FP_OUTPUT_PRODUCT: { type: "bar", orientation: "left", stackId: "consumption", direction: "minus" },
  FP_OUTPUT_RECEPT: { type: "bar", orientation: "left", stackId: "consumption", direction: "minus" },
  FP_OUTPUT_EXPIRE: { type: "bar", orientation: "left", stackId: "consumption", direction: "minus" },
  FP_OUTPUT_IN_PROCESS: { type: "bar", orientation: "left", stackId: "consumption", direction: "minus" },
  FP_OUTPUT_BY_PRODUCT: { type: "bar", orientation: "left", stackId: "consumption", direction: "minus" },
  FP_OUTPUT_SHIP: { type: "bar", orientation: "right", direction: "plus" }
};

// old PsiDetailChart.jsx는 getDefaultChartOptions(useAbsoluteValue=true)로 툴팁 값에 항상
// Math.abs()를 적용했다(direction:minus로 뒤집은 소비 지표도 부호 없이 보이는 이유) —
// PivotChartComposed(공용)의 기본 툴팁 포맷터는 음수면 "-"를 무조건 다시 붙여 화면별로 끌 수 없다.
// customTooltip으로 recharts 기본 툴팁(DefaultTooltipContent)을 재사용하면서 formatter만 교체한다
// (fp-report-screen-guide.md §A-7, ProdSalesStockStatus.js와 동일 패턴).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 공장>스테이지>품목>인벤토리>품목구분 5단. 품목구분(itemClassCd)은 같은
// 인벤토리(사실상 같은 품목) 아래에서 항상 같은 값이라 반복 표시되는 평범한 필드가 아니라
// ProdSalesStockStatus.js가 이미 쓰고 있는 것과 동일하게 그룹 레벨로 접는다(2026-08-11 사용자
// 지시 — 실제 데이터로 확인된 사실). 분석그룹(analysisGroup)/분석지표(analysisIndicator)는
// leaf 실제 데이터 행을 구분하는 값이라 여전히 그룹 필드에 넣지 않는다(MaterialPsi의
// analysisIndicator와 같은 계열).
const GROUP_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "itemCd", "itemNm", "inventoryCd", "inventoryNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CD_COL_ID = "itemCdGrp";
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const INVENTORY_CD_COL_ID = "inventoryCdGrp";
const INVENTORY_NM_COL_ID = `INVENTORY_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CLASS_CD_COL_ID = "itemClassCdGrp";
const ANALYSIS_GROUP_COL_ID = "analysisGroupGrp";

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";
const INVENTORY_NM_HIDE_KEY = "inventoryNmColsHide";

const ITEM_CLASS_OPTIONS = ["P", "I", "M"];

const PsiDetail = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // old InputField(itemClass, 기본값 'P') — 선택형 조건이라 값이 바뀌면 즉시 재조회한다(§A-9b).
  const [itemClassCd, setItemClassCd] = useState("P");
  // itemCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다. 자유 텍스트는 SearchOptsGroupRetrieve
  // (TextFieldSearch 기반) 안의 Enter/검색아이콘으로만 커밋한다.
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  // 응답 순서 뒤바뀜 방지 — 조회(그리드)와 차트는 서로 다른 요청이라 가드도 2개(§0-8).
  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (plant, stage, item, inventory) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState, itemClassCd });
  searchStateRef.current = { versionState, periodState, itemClassCd };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrievePsiDetail(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // 조회 전용 화면 — 공용 DefaultGridSetting의 defaultColDef.editable:true를 끈다.
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 핵심 지표(기초재고/기말재고/그룹합계) 강조는 행 전체가 아니라 분석 지표 컬럼~피벗 컬럼에만
      // cellStyle로 건다(아래 analysisIndicatorHighlightStyle/pivotCellStyle) — 배경색만 쓰고 굵게는
      // 안 쓴다(DP/MP/RP 전반의 green[50] 관례, MaterialPsi와 동일 기준). 공용 DataState.getRowStyles
      // (편집 상태 표시용, read-only 화면엔 무의미)를 끈다.
      getRowStyle: () => null,
      // 분석 그룹(analysisGroup) 셀 병합(spanRows) 활성화 — 값이 같아도 원본 행 순서상 서로 떨어진
      // 위치(기초재고=맨 앞, 기말재고=맨 뒤)에 다시 나타나야 해서 트리(행 그룹)로는 표현할 수 없다
      // (그룹핑은 값이 같으면 무조건 하나로 합침 — 순서 보존 불가). ResourceUtilization.js의
      // toolResourceYn과 동일하게 순서를 지키는 세로 병합으로 대체한다(2026-08-11 사용자 지시).
      enableCellSpan: true,
      // 행 그룹(트리) 3종 세트 — PlanResult/MaterialPsi와 동일한 조합(§A-6b).
      ...REPORT_TREE_GRID_OPTIONS
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 핵심 지표(기초재고/기말재고/그룹합계) 강조 — 분석 지표 컬럼에 건다. 배경색만(green[50]), 굵게는
  // 안 씀 — 아래 pivotCellStyle과 동일 기준으로 분석 지표 컬럼~피벗 컬럼 범위에만 적용된다.
  const analysisIndicatorHighlightStyle = (params) => {
    const indicator = params.data?.analysisIndicator;
    return (indicator === FP_ENDING_STOCK || indicator === FP_BEGINNING_STOCK || indicator === FP_GROUP_TOTAL)
      ? { backgroundColor: green[50] } : null;
  };

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/stageCd/stageNm/itemCd/itemNm/
  // inventoryCd/inventoryNm/itemClassCd/analysisGroup/analysisIndicator, 총 11개)와 1:1.
  // 공장/스테이지/품목/인벤토리는 행 그룹(트리), itemClassCd/analysisGroup/analysisIndicator는
  // 트리 leaf(인벤토리) 아래 실제 데이터 행을 구분하는 평범한 field 컬럼이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),
    // itemClassCd는 값 자체가 코드(P/I/M)라 다국어 라벨 변환이 필요한 유일한 그룹 필드다 — 위
    // GROUP_FIELDS.map이 만드는 범용 hidden 컬럼엔 valueFormatter를 못 넣어서(다른 필드는 코드
    // 그대로 노출돼도 되니 필요 없었다) 별도로 뺐다. 그룹 셀 렌더링이 이 hidden 필드 컬럼과
    // 아래 showRowGroup 표시 컬럼 중 어느 쪽 valueFormatter를 쓰는지 확실치 않아 양쪽에 동일하게
    // 건다(2026-08-11, 다국어 미적용 버그 수정).
    {
      headerName: "itemClassCd", field: "itemClassCd", colId: "itemClassCd", rowGroup: true, hide: true,
      suppressColumnsToolPanel: true,
      valueFormatter: (params) => (params.value ? t(`FP_ITEM_CLASS_CD_${params.value}`) : params.value)
    },
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY),
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 140
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY),
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE",
      hideKey: STAGE_NM_HIDE_KEY, width: 140
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_NM_HIDE_KEY),
      cdColId: ITEM_CD_COL_ID, cdField: "itemCd", cdHeader: "FP_ITEM_CD",
      nmColId: ITEM_NM_COL_ID, nmField: "itemNm", nmHeader: "FP_ITEM_NM",
      hideKey: ITEM_NM_HIDE_KEY, width: 130
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, INVENTORY_NM_HIDE_KEY),
      cdColId: INVENTORY_CD_COL_ID, cdField: "inventoryCd", cdHeader: "FP_INVENTORY_CD",
      nmColId: INVENTORY_NM_COL_ID, nmField: "inventoryNm", nmHeader: "FP_INVENTORY_NM",
      hideKey: INVENTORY_NM_HIDE_KEY, width: 130,
      // 품목구분 그룹 표시 컬럼을 인벤토리 명칭과 같은 화살표로 묶어서 초기엔 같이 숨겨두고,
      // 펼치면 같이 나타나게 한다(2026-08-11 사용자 지시) — 아래 itemClassCd 표시 컬럼의 초기
      // hide도 같은 hideKey(INVENTORY_NM_HIDE_KEY)를 읽어야 새로고침 후에도 어긋나지 않는다.
      extraDetailColumns: [ITEM_CLASS_CD_COL_ID]
    }),
    // TODO(MIG-T2): [psidetail/PsiDetail] 품목 그룹 footer("Route Total") 미이관.
    //   old: inventoryCd 컬럼정의의 groupFooter:{text:"Route Total"}(RealGrid 그룹 합계 행 텍스트,
    //        itemGroup을 접었을 때 표시)
    //   사유: 백엔드(ProdSalesStockStatusService#getPsiData)는 isDetail=true에서 합계 행의
    //        analysisIndicator를 FP_GROUP_TOTAL로만 채우고 inventoryCd/itemCd를 'SUM' 등으로 채우는
    //        합성 총계 행을 만들지 않는다(서비스 코드 확인) — AG Grid Row Grouping(§A-6b)엔 old
    //        RealGrid의 groupFooter 같은 "그룹 접힘 시 정적 텍스트 푸터" 개념이 없어 1차 범위에서 제외.

    // 품목구분 — 같은 인벤토리(사실상 같은 품목) 아래서 항상 같은 값이라(2026-08-11 사용자 확인)
    // 반복 표시하는 field 컬럼이 아니라 그룹(트리) 레벨로 접는다. 코드/명칭 쌍이 없는 단일 값
    // 그룹이라 ProdSalesStockStatus.js의 itemClassCd 처리와 동일하게 showRowGroup 컬럼 1개로 표시.
    {
      headerName: "FP_ITEM_CLASS_CD", colId: ITEM_CLASS_CD_COL_ID, showRowGroup: "itemClassCd", width: 100,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn", "centerAligned"],
      hide: readHide(reduxViewState, INVENTORY_NM_HIDE_KEY),
      cellRenderer: "agGroupCellRenderer", cellRendererParams: { suppressCount: true },
      valueFormatter: (params) => (params.value ? t(`FP_ITEM_CLASS_CD_${params.value}`) : params.value)
    },
    // 분석 그룹 — 같은 값(예: 재고)이 기초재고/기말재고처럼 원본 행 순서상 서로 떨어진 위치에
    // 다시 나타나야 해서 트리(행 그룹)로 접을 수 없다(그룹핑은 값이 같으면 무조건 하나로 합쳐
    // 순서가 무너진다, 2026-08-11 사용자 확인). 대신 ResourceUtilization.js의 toolResourceYn과
    // 동일하게 순서를 지키는 세로 병합(spanRows)으로 인접한 동일 값만 묶는다 — 같은 인벤토리/
    // 품목구분 경계를 넘어가지 않도록 두 필드도 같이 확인한다.
    {
      headerName: "FP_ANALYSIS_GROUP", field: "analysisGroup", colId: ANALYSIS_GROUP_COL_ID, width: 110,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => (params.value ? t(params.value) : params.value),
      // nodeA/nodeB는 nullable(SpanRowsParams) — 널 가드 없이 접근하면 조회 시 TypeError.
      spanRows: ({ nodeA, nodeB }) => (
        !!nodeA?.data && !!nodeB?.data && !nodeA.group && !nodeB.group &&
        nodeA.data.inventoryCd === nodeB.data.inventoryCd &&
        nodeA.data.itemClassCd === nodeB.data.itemClassCd &&
        nodeA.data.analysisGroup === nodeB.data.analysisGroup
      )
    },
    // leaf(분석 지표) — old displayCallback가 항상 transLangKey(value)로 번역한다.
    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator", width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => (params.value ? t(params.value) : params.value),
      cellStyle: analysisIndicatorHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old setCrossTabGridLayoutAndOptions의 styleCallback(피벗 데이터 셀 전용, 고정 컬럼엔 안 걸림) —
  // analysisIndicator가 기초재고/기말재고/그룹합계인 행은 green[50] 배경(굵게 없음, 위
  // analysisIndicatorHighlightStyle과 동일 기준).
  const pivotCellStyle = (params) => {
    const indicator = params.data?.analysisIndicator;
    return (indicator === FP_ENDING_STOCK || indicator === FP_BEGINNING_STOCK || indicator === FP_GROUP_TOTAL)
      ? { backgroundColor: green[50] } : null;
  };

  // 일버킷일 때 자식 헤더로 오는 요일 코드 번역+토/일 강조색은 common/grid.js
  // buildDayBucketPivotColumnDefs가 처리한다(old setColumnColorOnSunSat 대응, §A-6d/§A-6f).
  // pivotCellStyle은 이 화면 고유 강조라 childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: pivotCellStyle })
  });

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      inventoryCd: row.inventoryCd, inventoryNm: row.inventoryNm,
      itemClassCd: row.itemClassCd, analysisGroup: row.analysisGroup, analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period, itemClassCd: itemClass } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      itemCdNm: itemCdNmRef.current,
      itemClassCd: itemClass,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — 4단(공장>스테이지>품목>인벤토리)이라 FilterStatusPanel의 고정 슬롯
  // 순서(originLoc→destLoc→customer→item→workCenter→resource)에서 순서가 유지되는 4개
  // (1·2·4·5번)에 얹는다(PlanResult가 3단을 1·2·4번에 얹은 것과 동일한 절충, §A-7).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("FP_ITEM_NM"),
      workCenterCd: target.inventoryCd ?? null, workCenterCdHeader: t("FP_INVENTORY_CD"),
      workCenterNm: target.inventoryNm ?? null, workCenterNmHeader: t("FP_INVENTORY_NM")
    });
  };

  const leftAxisLabel = useMemo(() => `(${t("QTY")})`, [t]);
  const rightAxisLabel = useMemo(() => `${t("FP_OUTPUT_SHIP")}(${t("QTY")})`, [t]);

  // 클릭 기준으로 차트를 다시 조회한다. target = { plantCd, plantNm, stageCd, stageNm, itemCd, itemNm,
  // inventoryCd, inventoryNm } (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "",
      itemCd: target.itemCd ?? "", inventoryCd: target.inventoryCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.stageCd === nextParams.stageCd
      && prev.itemCd === nextParams.itemCd && prev.inventoryCd === nextParams.inventoryCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [psidetail/PsiDetail] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/stageCd/itemCd/inventoryCd/itemClassCd/analysisGroup/analysisIndicator에
    //        RealGrid autoFilter, 필터 선택값을 차트 조회의 changes(FilterParameter)로 함께 전송
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터 아이콘을
    //        띄울 자리가 없어졌다(가이드 §A-6e와 동일 사유, 트리거 UI 선례 없음). 백엔드는 changes를
    //        계속 받으므로 2차엔 트리거 UI만 얹으면 된다 — 지금은 빈 필터로 보낸다.
    fpPsiDetailService.getPsiDetailChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        // old `if (!conf) return null` — INDICATOR_CONFIG에 없는 지표(FP_GROUP_TOTAL 등)는 차트에서 제외.
        const knownRows = (data ?? []).filter(r => INDICATOR_CONFIG[r.analysisIndicator]);
        // old hasValue — 기초/기말재고는 항상 표시, 그 외는 전부 0/null이면 제외.
        const isMust = (indicator) => indicator === FP_BEGINNING_STOCK || indicator === FP_ENDING_STOCK;
        const rows = knownRows.filter(r => (
          isMust(r.analysisIndicator) || (r.pivotData ?? []).some(v => v != null && v !== 0)
        ));
        rows.sort((a, b) => INDICATOR_ORDER.indexOf(a.analysisIndicator) - INDICATOR_ORDER.indexOf(b.analysisIndicator));

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        // old makeChartDataset — 지표별로 한 행. direction:'minus'(소비 지표)만 음수로 뒤집어
        // 기초/기말재고 라인을 기준으로 위(공급)/아래(소비) 발란스 형태로 그린다.
        setChartData(rows.map(r => {
          const config = INDICATOR_CONFIG[r.analysisIndicator];
          const sign = config.direction === "minus" ? -1 : 1;
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => {
            const raw = r.pivotData?.[index];
            point[header] = raw != null ? sign * Math.abs(raw) : null;
          });
          return point;
        }));
        setChartSeries(rows.map(r => {
          const config = INDICATOR_CONFIG[r.analysisIndicator];
          const isLine = config.type === "line";
          return {
            field: r.analysisIndicator, text: r.analysisIndicator, type: config.type,
            groupName: config.orientation === "right" ? rightAxisLabel : leftAxisLabel,
            orientation: config.orientation,
            typeOptions: isLine
              ? { ...(config.dashed ? { strokeDasharray: "5 3" } : {}), name: t(r.analysisIndicator) }
              : { stackId: config.stackId, name: t(r.analysisIndicator) }
          };
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrievePsiDetail = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpPsiDetailService.getPsiDetails(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 현재 선택 셀도 그 행으로 옮긴다.
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  // 클릭 컬럼 → 드릴다운 레벨. old updateChartFromGrid의 `column.startsWith(...)` 분기를 트리 표시
  // 컬럼 colId 기준으로 옮긴 것 — 공장/스테이지/품목 칸이면 그 레벨까지만, 그 외(인벤토리/품목구분/
  // 분석그룹/분석지표/피벗 데이터 칸)는 4단 전체(인벤토리까지)를 그대로 쓴다.
  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const ITEM_LEVEL_COL_IDS = new Set([ITEM_CD_COL_ID, ITEM_NM_COL_ID]);

  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    if (ITEM_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        itemCd: row.itemCd, itemNm: row.itemNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult/MaterialPsi와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷/품목구분)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd, itemClassCd
  ]);

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan/
          // ResrcUtilization 2개 화면만 WITHOUT_HOUR, 그 외 전부(PsiDetail 포함) WITH_HOUR.
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        {/* 조회 버튼 없음(§A-9b, Report 화면 공통 정책) — 자유 텍스트(itemCdNm)는 필드 안의
            Enter/검색아이콘으로 수동 커밋. */}
        <SearchOptsGroupRetrieve
          fieldRefs={{ itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrievePsiDetail}
        />
        {/* old InputField(type="select", itemClass) — 독립 품목구분 필터, 기본값 'P'. 선택형이라
            값이 바뀌면 즉시 재조회(위 자동조회 effect 의존성에 itemClassCd 포함). */}
        <TextField
          select
          size="small"
          label={t("FP_ITEM_CLASS_CD")}
          value={itemClassCd}
          onChange={(e) => setItemClassCd(e.target.value)}
          sx={{ ml: 1.5, minWidth: 150 }}
        >
          {ITEM_CLASS_OPTIONS.map(code => (
            <MenuItem key={code} value={code}>{t(`FP_ITEM_CLASS_CD_${code}`)}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox의 sizes={[50, 50]}(direction="vertical") 대응 — SplitterContainer의
          기본 initialSizes([3, 2], 60/40)를 그대로 두면 그리드:차트가 1:1이 아니게 된다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-psi-detail-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        {/* old PsiDetailChart.jsx는 ChartLegendPlugin을 plugins 배열에 실제로 포함시켜 범례를
            보여줬다(`plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}`, 지표가 11종까지 늘어나
            범례가 정보 전달 역할을 한다) — MaterialPsi/ResourceUtilization과 동일하게 recharts 기본
            <Legend/>를 CSS로 숨기지 않고 그대로 노출한다. 클릭 시 series 숨김+취소선 인터랙션만
            PivotChartComposed에 대응 훅이 없어 미구현(§A-7 Report 공통 Tier2). */}
        {/* TODO(MIG-T2): [psidetail/PsiDetail] 차트 범례 클릭 시 시리즈 숨김+취소선 미이관.
              old: PsiDetailChart.jsx가 plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}로 실제 사용
              사유: recharts 기반 PivotChartComposed에 대응 훅이 없음 — §A-7 Report 카테고리 공통 Tier2 */}
        {/* old ChartAxisTitlePlugin의 좌우 y축 타이틀("(QTY)" / "FP_OUTPUT_SHIP(QTY)")은
            PivotChartComposed의 series.groupName 기반 네이티브 YAxis label로 그대로 재현된다
            (별도 플러그인 불필요, Tier2 아님 — MaterialPsi와 동일 판단). */}
        {/* TODO(MIG-T2): [psidetail/PsiDetail] 차트 장식성 인터랙션 2종 미이관.
              old: ChartHatchPatternPlugin(빗금 채우기), ChartDataLabelPlugin(막대 위 수치 직접 표시)
              사유: recharts/PivotChartComposed에 대응 훅이 없고, PivotChartComposed는 <Bar> children
                   슬롯을 안 열어둬 공용 파일 수정 없이는 표현 불가 — §A-7 Report 카테고리 공통 Tier2 */}
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", yAxisDomain: ["auto", "auto"], decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PsiDetail);
